<template>
  <div class="content-blocks-editor">
    <!-- Add Block Dropdown -->
    <div class="flex justify-between items-center mb-4">
      <span class="text-xs font-medium text-slate-500 uppercase tracking-wider">
        {{ blocks.length }} {{ blocks.length === 1 ? 'block' : 'blocks' }}
      </span>
      <div class="relative">
        <button
          type="button"
          @click="showAddMenu = !showAddMenu"
          class="px-3 py-1.5 border-2 border-neutral-900 text-xs font-semibold uppercase tracking-wider bg-white hover:bg-slate-50 transition-colors">
          + Add Block
        </button>
        <div
          v-if="showAddMenu"
          class="absolute right-0 mt-1 w-48 bg-white border-2 border-neutral-900 shadow-lg z-10">
          <button
            v-for="blockType in blockTypes"
            :key="blockType.type"
            type="button"
            @click="addBlock(blockType.type)"
            class="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 border-b border-slate-200 last:border-b-0">
            {{ blockType.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Blocks List -->
    <div class="space-y-4">
      <div
        v-for="(block, index) in blocks"
        :key="block.id"
        class="iso-card-sm border-2 border-neutral-900 bg-white"
        draggable="true"
        @dragstart="onDragStart($event, index)"
        @dragover.prevent="onDragOver($event, index)"
        @drop="onDrop($event, index)">
        <!-- Block Header -->
        <div class="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200 cursor-move">
          <div class="flex items-center gap-2">
            <span class="text-slate-400">&#x2630;</span>
            <span class="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              {{ getBlockTypeLabel(block.type) }}
            </span>
          </div>
          <div class="flex items-center gap-1">
            <button
              type="button"
              @click="moveBlock(index, -1)"
              :disabled="index === 0"
              class="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
              title="Move up">
              &#x2191;
            </button>
            <button
              type="button"
              @click="moveBlock(index, 1)"
              :disabled="index === blocks.length - 1"
              class="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
              title="Move down">
              &#x2193;
            </button>
            <button
              type="button"
              @click="removeBlock(index)"
              class="p-1 text-red-400 hover:text-red-600"
              title="Delete block">
              &#x2715;
            </button>
          </div>
        </div>

        <!-- Block Content Editor -->
        <div class="p-4">
          <!-- Text Block -->
          <template v-if="block.type === 'text'">
            <textarea
              v-model="block.content"
              @input="emitUpdate"
              rows="4"
              class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
              placeholder="Enter text content (supports markdown: **bold**, *italic*, [links](url))" />
          </template>

          <!-- Heading Block -->
          <template v-else-if="block.type === 'heading'">
            <div class="flex gap-2">
              <select
                v-model.number="block.level"
                @change="emitUpdate"
                class="px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500">
                <option :value="2">
                  H2
                </option>
                <option :value="3">
                  H3
                </option>
                <option :value="4">
                  H4
                </option>
              </select>
              <input
                v-model="block.content"
                @input="emitUpdate"
                type="text"
                class="flex-1 px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Heading text">
            </div>
          </template>

          <!-- Image Block -->
          <template v-else-if="block.type === 'image'">
            <div class="space-y-2">
              <input
                v-model="block.url"
                @input="emitUpdate"
                type="url"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Image URL">
              <input
                v-model="block.alt"
                @input="emitUpdate"
                type="text"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Alt text">
              <input
                v-model="block.caption"
                @input="emitUpdate"
                type="text"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Caption (optional)">
            </div>
          </template>

          <!-- Video Block -->
          <template v-else-if="block.type === 'video'">
            <div class="space-y-2">
              <input
                v-model="block.embed_url"
                @input="emitUpdate"
                type="url"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Video embed URL (YouTube, Vimeo)">
              <input
                v-model="block.caption"
                @input="emitUpdate"
                type="text"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Caption (optional)">
            </div>
          </template>

          <!-- FAQ Block -->
          <template v-else-if="block.type === 'faq'">
            <div class="space-y-2">
              <input
                v-model="block.question"
                @input="emitUpdate"
                type="text"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Question">
              <textarea
                v-model="block.answer"
                @input="emitUpdate"
                rows="3"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Answer" />
            </div>
          </template>

          <!-- Callout Block -->
          <template v-else-if="block.type === 'callout'">
            <div class="space-y-2">
              <select
                v-model="block.style"
                @change="emitUpdate"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500">
                <option value="info">
                  Info
                </option>
                <option value="warning">
                  Warning
                </option>
                <option value="tip">
                  Tip
                </option>
                <option value="important">
                  Important
                </option>
              </select>
              <textarea
                v-model="block.content"
                @input="emitUpdate"
                rows="3"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Callout content" />
            </div>
          </template>

          <!-- Quote Block -->
          <template v-else-if="block.type === 'quote'">
            <div class="space-y-2">
              <textarea
                v-model="block.content"
                @input="emitUpdate"
                rows="3"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Quote text" />
              <input
                v-model="block.attribution"
                @input="emitUpdate"
                type="text"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Attribution (optional)">
            </div>
          </template>

          <!-- List Block -->
          <template v-else-if="block.type === 'list'">
            <div class="space-y-2">
              <select
                v-model="block.style"
                @change="emitUpdate"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500">
                <option value="unordered">
                  Bullet List
                </option>
                <option value="ordered">
                  Numbered List
                </option>
              </select>
              <div class="space-y-1">
                <div
                  v-for="(item, itemIndex) in block.items"
                  :key="itemIndex"
                  class="flex gap-2">
                  <input
                    v-model="block.items[itemIndex]"
                    @input="emitUpdate"
                    type="text"
                    class="flex-1 px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                    :placeholder="`Item ${itemIndex + 1}`">
                  <button
                    type="button"
                    @click="removeListItem(index, itemIndex)"
                    class="px-2 text-red-400 hover:text-red-600">
                    &#x2715;
                  </button>
                </div>
                <button
                  type="button"
                  @click="addListItem(index)"
                  class="text-xs text-slate-500 hover:text-slate-700">
                  + Add item
                </button>
              </div>
            </div>
          </template>

          <!-- Code Block -->
          <template v-else-if="block.type === 'code'">
            <div class="space-y-2">
              <input
                v-model="block.language"
                @input="emitUpdate"
                type="text"
                class="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Language (e.g., javascript, python)">
              <textarea
                v-model="block.content"
                @input="emitUpdate"
                rows="6"
                class="w-full px-3 py-2 border border-slate-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Code content" />
            </div>
          </template>

          <!-- Table Block -->
          <template v-else-if="block.type === 'table'">
            <div class="space-y-3">
              <!-- Headers -->
              <div>
                <p class="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                  Headers
                </p>
                <div class="flex flex-wrap gap-2">
                  <input
                    v-for="(header, hIndex) in block.headers"
                    :key="'h-' + hIndex"
                    v-model="block.headers[hIndex]"
                    @input="emitUpdate"
                    type="text"
                    class="flex-1 min-w-[120px] px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                    :placeholder="'Header ' + (hIndex + 1)">
                  <button
                    type="button"
                    @click="addTableColumn(index)"
                    class="px-3 py-2 text-xs text-slate-500 hover:text-slate-700 border border-dashed border-slate-300">
                    + Column
                  </button>
                </div>
              </div>
              <!-- Rows -->
              <div>
                <p class="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                  Rows
                </p>
                <div class="space-y-2">
                  <div
                    v-for="(row, rIndex) in block.rows"
                    :key="'r-' + rIndex"
                    class="flex gap-2">
                    <input
                      v-for="(cell, cIndex) in row"
                      :key="'c-' + cIndex"
                      v-model="block.rows[rIndex][cIndex]"
                      @input="emitUpdate"
                      type="text"
                      class="flex-1 min-w-[120px] px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                      :placeholder="block.headers[cIndex] || 'Cell'">
                    <button
                      type="button"
                      @click="removeTableRow(index, rIndex)"
                      class="px-2 text-red-400 hover:text-red-600">
                      &#x2715;
                    </button>
                  </div>
                  <button
                    type="button"
                    @click="addTableRow(index)"
                    class="text-xs text-slate-500 hover:text-slate-700">
                    + Add row
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-if="blocks.length === 0"
      class="text-center py-12 border-2 border-dashed border-slate-300 rounded">
      <p class="text-slate-500 mb-4">
        No content blocks yet
      </p>
      <button
        type="button"
        @click="showAddMenu = true"
        class="px-4 py-2 border-2 border-neutral-900 text-xs font-semibold uppercase tracking-wider bg-white hover:bg-slate-50 transition-colors">
        Add Your First Block
      </button>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'

export default {
  name: 'ContentBlocksEditor',

  props: {
    initialBlocks: {
      type: Array,
      default: () => []
    },
    onUpdate: {
      type: Function,
      required: true
    }
  },

  setup(props) {
    // Normalize blocks to handle legacy schema formats
    const normalizeBlock = (block) => {
      const normalized = { ...block }

      // Heading: convert string level ("h2") to integer (2)
      if (block.type === 'heading' && typeof block.level === 'string') {
        const match = block.level.match(/h(\d)/)
        normalized.level = match ? parseInt(match[1], 10) : 2
      }

      // Video: migrate 'url' to 'embed_url'
      if (block.type === 'video' && block.url && !block.embed_url) {
        normalized.embed_url = block.url
        delete normalized.url
      }

      // Table: migrate CSV 'content' to headers/rows arrays
      if (block.type === 'table' && block.content && !block.headers) {
        const lines = block.content.split('\n').filter((l) => l.trim())
        if (lines.length > 0) {
          normalized.headers = lines[0].split(',').map((h) => h.trim())
          normalized.rows = lines.slice(1).map((line) => line.split(',').map((c) => c.trim()))
        } else {
          normalized.headers = ['Column 1', 'Column 2']
          normalized.rows = [['', '']]
        }
        delete normalized.content
      } else if (block.type === 'table' && !block.headers) {
        normalized.headers = ['Column 1', 'Column 2']
        normalized.rows = [['', '']]
      }

      return normalized
    }

    const blocks = ref(props.initialBlocks.map((b, i) => ({
      ...normalizeBlock(b),
      id: b.id || `block-${i}-${Date.now()}`
    })))
    const showAddMenu = ref(false)
    const dragIndex = ref(null)

    const blockTypes = [
      { type: 'text', label: 'Text' },
      { type: 'heading', label: 'Heading' },
      { type: 'image', label: 'Image' },
      { type: 'video', label: 'Video' },
      { type: 'faq', label: 'FAQ' },
      { type: 'callout', label: 'Callout' },
      { type: 'quote', label: 'Quote' },
      { type: 'list', label: 'List' },
      { type: 'code', label: 'Code' },
      { type: 'table', label: 'Table' }
    ]

    const getBlockTypeLabel = (type) => {
      const found = blockTypes.find((bt) => bt.type === type)
      return found ? found.label : type
    }

    const emitUpdate = () => {
      // Remove the internal id before sending to parent
      const cleanBlocks = blocks.value.map(({ id: _id, ...rest }) => rest)
      props.onUpdate(cleanBlocks)
    }

    const addBlock = (type) => {
      const newBlock = { id: `block-${Date.now()}`, type }

      switch (type) {
        case 'text':
          newBlock.content = ''
          break
        case 'heading':
          newBlock.level = 2 // Integer, not string
          newBlock.content = ''
          break
        case 'image':
          newBlock.url = ''
          newBlock.alt = ''
          newBlock.caption = ''
          break
        case 'video':
          newBlock.embed_url = '' // Match renderer's expected field name
          newBlock.caption = ''
          break
        case 'faq':
          newBlock.question = ''
          newBlock.answer = ''
          break
        case 'callout':
          newBlock.style = 'info'
          newBlock.content = ''
          break
        case 'quote':
          newBlock.content = ''
          newBlock.attribution = ''
          break
        case 'list':
          newBlock.style = 'unordered'
          newBlock.items = ['']
          break
        case 'code':
          newBlock.language = ''
          newBlock.content = ''
          break
        case 'table':
          newBlock.headers = ['Column 1', 'Column 2'] // Array of headers
          newBlock.rows = [['', '']] // 2D array of rows
          break
      }

      blocks.value.push(newBlock)
      showAddMenu.value = false
      emitUpdate()
    }

    const removeBlock = (index) => {
      if (confirm('Are you sure you want to delete this block?')) {
        blocks.value.splice(index, 1)
        emitUpdate()
      }
    }

    const moveBlock = (index, direction) => {
      const newIndex = index + direction
      if (newIndex < 0 || newIndex >= blocks.value.length) return

      const block = blocks.value.splice(index, 1)[0]
      blocks.value.splice(newIndex, 0, block)
      emitUpdate()
    }

    const addListItem = (blockIndex) => {
      blocks.value[blockIndex].items.push('')
      emitUpdate()
    }

    const removeListItem = (blockIndex, itemIndex) => {
      blocks.value[blockIndex].items.splice(itemIndex, 1)
      emitUpdate()
    }

    // Table helpers
    const addTableColumn = (blockIndex) => {
      const block = blocks.value[blockIndex]
      block.headers.push('')
      // Add empty cell to each existing row
      block.rows.forEach((row) => row.push(''))
      emitUpdate()
    }

    const addTableRow = (blockIndex) => {
      const block = blocks.value[blockIndex]
      const colCount = block.headers.length
      block.rows.push(Array(colCount).fill(''))
      emitUpdate()
    }

    const removeTableRow = (blockIndex, rowIndex) => {
      blocks.value[blockIndex].rows.splice(rowIndex, 1)
      emitUpdate()
    }

    // Drag and drop handlers
    const onDragStart = (event, index) => {
      dragIndex.value = index
      event.dataTransfer.effectAllowed = 'move'
    }

    const onDragOver = (event, _index) => {
      event.dataTransfer.dropEffect = 'move'
    }

    const onDrop = (event, index) => {
      if (dragIndex.value === null || dragIndex.value === index) return

      const block = blocks.value.splice(dragIndex.value, 1)[0]
      blocks.value.splice(index, 0, block)
      dragIndex.value = null
      emitUpdate()
    }

    return {
      blocks,
      showAddMenu,
      blockTypes,
      getBlockTypeLabel,
      emitUpdate,
      addBlock,
      removeBlock,
      moveBlock,
      addListItem,
      removeListItem,
      addTableColumn,
      addTableRow,
      removeTableRow,
      onDragStart,
      onDragOver,
      onDrop
    }
  }
}
</script>

<style scoped>
.content-blocks-editor {
  min-height: 200px;
}

[draggable="true"] {
  cursor: grab;
}

[draggable="true"]:active {
  cursor: grabbing;
}
</style>
